import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Share2, Download, Mail } from 'lucide-react';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { bestPhotoFor } from '@/lib/photo';
// Phase 246 — Canonical DD/MM/YYYY for customer-facing artifacts.
import { formatGlobalDate } from '@/utils/dateFormatter';

// Phase 81 — Elite Scout-Grade Progress Report Card
// Redesigned for clarity, density, and parent-ready professionalism.

export const DigitalAthleteCard = ({ student, progressCard, onShare }) => {
  if (!progressCard || !student) return null;

  const skills = Object.entries(progressCard.skills_rating || {});
  const radarData = skills.map(([skill, value]) => ({
    skill: skill.charAt(0).toUpperCase() + skill.slice(1),
    value, fullMark: 5,
  }));
  const avgRating = skills.length > 0
    ? (skills.reduce((sum, [, v]) => sum + v, 0) / skills.length).toFixed(1)
    : 0;
  const overallPct = Math.round((avgRating / 5) * 100);
  const height = student.height || 0;
  const weight = student.weight || 0;
  const bmi = (height > 0 && weight > 0)
    ? (weight / Math.pow(height / 100, 2)).toFixed(1)
    : '-';

  const getPerf = (pct) => {
    if (pct >= 80) return { label: 'Elite',      color: 'bg-emerald-600', text: 'text-emerald-700', tint: 'from-emerald-500 to-teal-600' };
    if (pct >= 65) return { label: 'Advanced',   color: 'bg-blue-600',    text: 'text-blue-700',    tint: 'from-blue-500 to-indigo-600' };
    if (pct >= 50) return { label: 'Developing', color: 'bg-amber-600',   text: 'text-amber-700',   tint: 'from-amber-500 to-orange-600' };
    return                { label: 'Beginner',   color: 'bg-slate-600',   text: 'text-slate-700',   tint: 'from-slate-500 to-slate-700' };
  };
  const perf = getPerf(overallPct);

  const handleShareWhatsApp = () => {
    const msg = `🏆 ${student.name}'s Athletic Progress Report\n\n` +
      `⭐ Overall: ${avgRating}/5.0 (${perf.label})\n` +
      `📊 Course: ${student.course || student.sport || 'N/A'}\n` +
      `👥 Batch: ${student.batch || 'N/A'}\n\n` +
      `💪 Top Skills:\n` +
      skills.sort((a, b) => b[1] - a[1]).slice(0, 3)
        .map(([s, v]) => `  • ${s.charAt(0).toUpperCase() + s.slice(1)}: ${v}/5`).join('\n') +
      `\n\n📱 Track on Out-Play.in`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
    toast.success('Opening WhatsApp...');
  };

  const handleShareEmail = () => {
    const subject = `${student.name}'s Athletic Progress Report — ${formatGlobalDate(new Date())}`;
    const body = `View ${student.name}'s latest progress report.\n\nOverall: ${avgRating}/5.0\nCourse: ${student.course || student.sport || 'N/A'}\nBatch: ${student.batch || 'N/A'}\n\nTracked via Out-Play.in`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    toast.success('Opening email client...');
  };

  // Phase 81 — Clean A4 single-page Print/PDF view (parent-ready)
  const handleDownload = () => {
    const skillRows = skills.map(([s, r]) => `
      <tr>
        <td style="padding:6px 0;text-transform:capitalize;font-size:13px;color:#0f172a">${s}</td>
        <td style="padding:6px 0;text-align:right">
          <div style="display:inline-block;width:120px;height:8px;background:#e2e8f0;border-radius:9999px;overflow:hidden;vertical-align:middle">
            <div style="width:${(r/5)*100}%;height:100%;background:${r>=4?'#10b981':r>=3?'#3b82f6':'#f59e0b'}"></div>
          </div>
          <span style="display:inline-block;width:36px;text-align:right;font-weight:700;font-size:13px;color:#0f172a;margin-left:8px">${r}/5</span>
        </td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
      <html><head>
      <title>${student.name} — Progress Report</title>
      <meta charset="utf-8" />
      <style>
        @page { size: A4; margin: 12mm; }
        * { box-sizing: border-box; }
        body { font-family: -apple-system, 'Segoe UI', Inter, sans-serif; margin: 0; color: #0f172a; }
        .wrap { max-width: 760px; margin: 0 auto; }
        .header { background: linear-gradient(135deg, #0f766e, #14b8a6); color: white; padding: 22px 28px; border-radius: 14px; display: flex; justify-content: space-between; align-items: center; }
        .header h1 { margin: 0; font-size: 20px; letter-spacing: 0.5px; }
        .header .sub { font-size: 11px; opacity: 0.85; margin-top: 4px; }
        .badge { background: rgba(255,255,255,0.22); padding: 6px 14px; border-radius: 9999px; font-size: 12px; font-weight: 700; letter-spacing: 0.5px; }
        .row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin: 16px 0; }
        .stat { background: #f1f5f9; padding: 12px; border-radius: 10px; text-align: center; }
        .stat .l { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
        .stat .v { font-size: 18px; font-weight: 700; margin-top: 4px; }
        .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .panel { background: white; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; }
        .panel h3 { margin: 0 0 10px; font-size: 12px; text-transform: uppercase; color: #475569; letter-spacing: 0.5px; }
        table { width: 100%; border-collapse: collapse; }
        .remarks { background: #faf5ff; border-left: 4px solid #8b5cf6; padding: 12px 16px; margin: 16px 0; border-radius: 8px; }
        .remarks .l { font-size: 10px; color: #6b21a8; font-weight: 700; text-transform: uppercase; }
        .remarks p { font-style: italic; color: #475569; margin: 6px 0 0; line-height: 1.5; font-size: 13px; }
        .remarks .by { font-size: 11px; color: #94a3b8; margin-top: 6px; }
        .footer { text-align: center; color: #94a3b8; font-size: 10px; margin-top: 22px; }
        @media print { .wrap { max-width: none; } }
      </style>
      </head><body>
      <div class="wrap">
        <div class="header">
          <div>
            <h1>ATHLETIC PROGRESS REPORT</h1>
            <div class="sub">${student.name} · ${student.custom_id || ''} · ${progressCard.created_at?.slice(0, 10) || ''}</div>
          </div>
          <div class="badge">${perf.label.toUpperCase()} · ${avgRating}/5.0</div>
        </div>
        <div class="row">
          <div class="stat"><div class="l">Course</div><div class="v">${student.course || student.sport || 'N/A'}</div></div>
          <div class="stat"><div class="l">Batch</div><div class="v">${student.batch || 'N/A'}</div></div>
          <div class="stat"><div class="l">Overall</div><div class="v">${avgRating}/5.0</div></div>
        </div>
        <div class="row">
          <div class="stat"><div class="l">Height</div><div class="v">${height || '-'} cm</div></div>
          <div class="stat"><div class="l">Weight</div><div class="v">${weight || '-'} kg</div></div>
          <div class="stat"><div class="l">BMI</div><div class="v">${bmi}</div></div>
        </div>
        <div class="panel">
          <h3>Skills Assessment</h3>
          <table>${skillRows}</table>
        </div>
        ${progressCard.comments ? `
        <div class="remarks">
          <div class="l">Coach Remarks</div>
          <p>"${progressCard.comments}"</p>
          <div class="by">— ${progressCard.coach_name || 'Coach'}</div>
        </div>` : ''}
        <div class="footer">Generated by Out-Play.in · ${formatGlobalDate(new Date())}</div>
      </div>
      <script>window.onload = () => setTimeout(() => window.print(), 400);</script>
      </body></html>`;

    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    toast.success('A4 print view opened. Use Save as PDF.');
  };

  return (
    <Card className="overflow-hidden border-2 border-primary/15 shadow-md hover:shadow-lg transition-shadow" data-testid="digital-athlete-card">
      {/* ===== Compact Header Bar (Phase 81: single-line identity + floating rating badge) ===== */}
      <div className={`bg-gradient-to-r ${perf.tint} px-4 py-2.5 text-white relative`}>
        <div className="flex items-center justify-between gap-2 min-w-0">
          {/* Left: Avatar + Name + ID (truncates with ellipsis) */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {bestPhotoFor(student) ? (
              <img
                src={bestPhotoFor(student)}
                alt={student.name || ''}
                className="h-9 w-9 rounded-full object-cover border-2 border-white/40 shrink-0"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                data-testid="athlete-photo"
              />
            ) : (
              <div className="h-9 w-9 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center shrink-0 text-xs font-bold">
                {(student.name || 'A').split(' ').slice(0, 2).map(s => s[0]).join('').toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <h3 className="text-sm font-display font-bold tracking-wide truncate" data-testid="athlete-name" title={student.name}>
                {(student.name || 'Athlete').toUpperCase()}
              </h3>
              <p className="text-[10px] text-white/80 font-mono truncate">
                {student.custom_id || student.id?.slice(0, 8) || '—'} · {progressCard.created_at?.slice(0, 10) || ''}
              </p>
            </div>
          </div>
          {/* Right: Floating Overall Rating badge */}
          <div className="flex flex-col items-end shrink-0" data-testid="rating-badge">
            <div className="bg-white/25 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1.5 border border-white/30">
              <span className="text-lg font-display font-bold tabular-nums leading-none">{avgRating}</span>
              <span className="text-[10px] opacity-80 leading-none">/5.0</span>
            </div>
            <span className="text-[9px] uppercase tracking-wider mt-1 opacity-90">{perf.label}</span>
          </div>
        </div>

        {/* Sub-row: compact course/batch chips */}
        <div className="flex flex-wrap items-center gap-1 mt-2">
          {(student.course || student.sport) && (
            <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded font-medium truncate max-w-[140px]" title={student.course || student.sport}>
              {student.course || student.sport}
            </span>
          )}
          {student.batch && (
            <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded font-medium truncate max-w-[140px]" title={student.batch}>
              {student.batch}
            </span>
          )}
          {height > 0 && <span className="text-[10px] bg-white/15 px-1.5 py-0.5 rounded">{height}cm</span>}
          {weight > 0 && <span className="text-[10px] bg-white/15 px-1.5 py-0.5 rounded">{weight}kg</span>}
          {bmi !== '-' && <span className="text-[10px] bg-white/15 px-1.5 py-0.5 rounded">BMI {bmi}</span>}
        </div>
      </div>

      {/* ===== Body: Radar (centred, slightly smaller) + Skills 2-column ===== */}
      <div className="grid md:grid-cols-5 gap-3 p-4">
        {/* Radar — centred with breathing room (Phase 81: smaller radius for more padding) */}
        <div className="md:col-span-2 flex flex-col items-center justify-center" data-testid="radar-chart">
          <ResponsiveContainer width="100%" height={210}>
            <RadarChart data={radarData} outerRadius="78%" margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
              <PolarGrid stroke="#cbd5e1" strokeDasharray="2 4" />
              <PolarAngleAxis dataKey="skill" tick={{ fill: '#475569', fontSize: 9 }} />
              <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fill: '#94a3b8', fontSize: 8 }} />
              <Radar dataKey="value" stroke="#0f766e" fill="#0f766e" fillOpacity={0.55} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
          <div className="h-1.5 w-32 bg-muted rounded-full overflow-hidden mt-1">
            <div className={`h-full ${perf.color} transition-all duration-500`} style={{ width: `${overallPct}%` }} />
          </div>
          <p className="text-[10px] text-muted-foreground mt-1.5">{overallPct}% performance</p>
        </div>

        {/* Skills (2-column) */}
        <div className="md:col-span-3 space-y-2" data-testid="skills-section">
          <h5 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Top Skills</h5>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
            {skills.sort((a, b) => b[1] - a[1]).map(([skill, rating]) => {
              const pct = (rating / 5) * 100;
              const color = pct >= 80 ? 'bg-emerald-500'
                            : pct >= 60 ? 'bg-blue-500'
                            : pct >= 40 ? 'bg-amber-500'
                            : 'bg-rose-500';
              return (
                <div key={skill} className="flex items-center gap-1.5" data-testid={`skill-${skill}`}>
                  <span className="text-[11px] capitalize w-14 truncate text-slate-700 dark:text-slate-300" title={skill}>{skill}</span>
                  <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[10px] font-bold tabular-nums w-6 text-right">{rating}</span>
                </div>
              );
            })}
          </div>

          {/* Coach Remarks — expanded, italic, smaller font (Phase 81) */}
          {progressCard.comments && (
            <div className="mt-3 p-2.5 rounded-md bg-purple-50/60 dark:bg-purple-950/20 border-l-2 border-purple-400" data-testid="coach-remarks">
              <p className="text-[9px] text-purple-700 font-bold uppercase tracking-wider mb-1">Coach Remarks</p>
              <p className="text-[11px] italic leading-relaxed text-slate-700 dark:text-slate-300 break-words" style={{ minHeight: '32px' }}>
                "{progressCard.comments}"
              </p>
              <p className="text-[9px] text-purple-600 mt-1.5">— {progressCard.coach_name || 'Coach'}</p>
            </div>
          )}
        </div>
      </div>

      {/* ===== Footer Action Bar ===== */}
      <div className="px-4 pb-3 pt-1 flex gap-1.5 border-t border-border/50 bg-muted/20">
        <Button size="sm" className="flex-1 h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700" onClick={handleShareWhatsApp} data-testid="share-whatsapp-button">
          <Share2 className="h-3 w-3 mr-1" /> WhatsApp
        </Button>
        <Button size="sm" variant="outline" className="flex-1 h-7 text-[11px]" onClick={handleShareEmail} data-testid="share-email-button">
          <Mail className="h-3 w-3 mr-1" /> Email
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-[11px] px-3" onClick={handleDownload} data-testid="download-card-button">
          <Download className="h-3 w-3 mr-1" /> A4 PDF
        </Button>
      </div>
    </Card>
  );
};
